import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimationElementReferenceModel, MapElementReferenceModel } from "../../../../shared/action/MapElementReferenceModel";
import { MapEditorWindow } from "./windows/MapEditorWindow";
import { Selector } from "./Selector";
import { MdErrorOutline } from "react-icons/md";
import { selectorMapEditorStore, EditorToolType } from "../../../stores/MapEditorStore";
import { undoableMapEditorSelectOtherPlacementElement } from "../../../stores/undo/operation/SetPlacementSelectionOp";
import { OtherPlacementElement } from "../../../stores/MapRelatedStore";
import { observer } from "mobx-react-lite";
import { editorMapStore, MapStatus } from "../../../stores/EditorMapStore";
import { LoadingSpinnerBlack, LoadingSpinnerSmall } from "../../shared/LoadingSpinner";
import { actionEditorStore } from "../../../stores/ActionEditorStore";
import { MapElementErrorIcon, NoMapElementIcon } from "../../editor/ElementIcons";
export const MapElementSelector = observer(({
  selectedElement,
  elementSetter,
  elementsGetter,
  allowBlankValue,
  parameterTypes,
  limitedToMapId
}) => {
  const {
    t
  } = useTranslation();

  const getDefaultSelectedMapId = () => {
    if (limitedToMapId) {
      return limitedToMapId;
    }

    if (!selectedElement.isElementIdSet() && !selectedElement.isMapSet()) {
      const mapId = actionEditorStore.mapElementSelectorLatestSelectedMap;

      if (editorMapStore.hasMapId(mapId)) {
        return mapId;
      }
    }

    return selectedElement.mapId;
  };

  const [selectedMapId, setSelectedMapId] = useState(getDefaultSelectedMapId);
  const selectedMapElements = elementsGetter(selectedMapId);
  const selectedElementMapElements = selectedMapId === selectedElement.mapId ? selectedMapElements : elementsGetter(selectedElement.mapId);
  const userIsAllowedToEditSelectedMap = editorMapStore.isUserAllowedToEditMapWithId(selectedMapId);
  const hasAreaTriggers = Boolean(parameterTypes === null || parameterTypes === void 0 ? void 0 : parameterTypes.some(parameterType => parameterType === "actions/AreaTriggerValueModel"));
  const shouldShowEmptyAreaTriggers = hasAreaTriggers && userIsAllowedToEditSelectedMap;
  let icon = /*#__PURE__*/React.createElement(NoMapElementIcon, null);
  let label = undefined;
  let smallerLabel = undefined;
  let loadingStatusIndicator = undefined;
  const selectedMapStatus = selectedMapElements.mapStatus;
  const selectedElementMapStatus = selectedElementMapElements.mapStatus;
  const selectedElementOnMap = selectedElementMapElements.elements.find(element => element.id === selectedElement.elementId);

  if (selectedElementMapStatus === MapStatus.Loaded) {
    if (selectedElementOnMap) {
      label = selectedElementOnMap.label;

      if (selectedElementMapElements.mapName) {
        smallerLabel = `\n(${selectedElementMapElements.mapName})`;
      }
    } else {
      if (selectedElement.isComplete()) {
        icon = /*#__PURE__*/React.createElement(MapElementErrorIcon, null);
        label = t("action_editor.element_not_available_anymore");
      } else {
        label = t("action_editor.no_map_element_set");
      }
    }
  } else {
    switch (selectedElementMapStatus) {
      case MapStatus.NotRequested:
      case MapStatus.Loading:
        icon = /*#__PURE__*/React.createElement(LoadingSpinnerBlack, null);
        label = t("game.map_loading");
        break;

      case MapStatus.DoesNotExist:
        icon = /*#__PURE__*/React.createElement(MapElementErrorIcon, null);
        label = t("action_editor.element_not_available_anymore");
        break;

      default:
        throw new Error("Not implemented: " + selectedElementMapStatus);
    }
  }

  switch (selectedMapStatus) {
    case MapStatus.Loaded:
      break;

    case MapStatus.NotRequested:
    case MapStatus.Loading:
      loadingStatusIndicator = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(LoadingSpinnerSmall, null), " ", t("game.map_loading"));
      break;

    case MapStatus.DoesNotExist:
      loadingStatusIndicator = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(MdErrorOutline, null), " ", t("action_editor.map_does_not_exist"));
      break;

    default:
      throw new Error("Not implemented: " + selectedMapStatus);
  }

  const buttonInvalid = () => {
    return !allowBlankValue && !selectedElementOnMap;
  };

  const setMap = mapId => {
    mapId = mapId || 0;
    setSelectedMapId(mapId);
    actionEditorStore.setMapElementSelectorLatestSelectedMap(mapId);
  };

  const setElementId = elementId => {
    if (selectedElement instanceof AnimationElementReferenceModel) {
      elementSetter(new AnimationElementReferenceModel({
        mapId: selectedMapId,
        elementId
      }));
      return;
    }

    elementSetter(new MapElementReferenceModel({
      mapId: selectedMapId,
      elementId
    }));
  };

  const openEditor = (selectAndClose, closeEditor) => {
    return /*#__PURE__*/React.createElement(MapEditorWindow, {
      selectedMapElements: selectedMapElements,
      selectElement: selectAndClose,
      closePopup: closeEditor,
      shouldShowEmptyAreaTriggers: shouldShowEmptyAreaTriggers
    });
  };

  let placementElementByParameterType = null;

  if (parameterTypes !== null && parameterTypes !== void 0 && parameterTypes.some(type => type === "actions/MapMarkerValueModel")) {
    placementElementByParameterType = OtherPlacementElement.MapMarker;
  } else if (parameterTypes !== null && parameterTypes !== void 0 && parameterTypes.some(type => type === "actions/AreaTriggerValueModel")) {
    placementElementByParameterType = OtherPlacementElement.AreaTrigger;
  }

  const prepareNewElement = () => {
    selectorMapEditorStore.setTool(EditorToolType.PlaceAsset);
    undoableMapEditorSelectOtherPlacementElement(placementElementByParameterType, selectorMapEditorStore);
  };

  return /*#__PURE__*/React.createElement(Selector, {
    currentElement: selectedElementOnMap,
    valueSetter: setElementId,
    mapId: selectedMapId,
    mapSetter: setMap,
    limitedToMapId: limitedToMapId,
    loadingStatusIndicator: loadingStatusIndicator,
    newString: "editor.element_new",
    filteredListGetter: () => selectedMapElements.elements,
    getElementValue: element => element ? element.id : "",
    getElementName: element => element === null || element === void 0 ? void 0 : element.label,
    getElementImageUrl: element => element === null || element === void 0 ? void 0 : element.image,
    getElementIcon: element => element ? element.icon : icon,
    prepareNewElement: placementElementByParameterType ? prepareNewElement : null,
    buttonText: label,
    buttonSecondaryText: smallerLabel,
    buttonInvalidProvider: buttonInvalid,
    selectableOnMap: true,
    editorView: openEditor,
    onPopupOpen: () => {
      setSelectedMapId(getDefaultSelectedMapId());
    }
  });
});